import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Download, Trash2, FileText, FileSpreadsheet, FileImage,
  FileArchive, FileCode, File as FileIcon, Loader2, Sparkles,
} from "lucide-react";

function humanSize(n) {
  if (!n) return "";
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function iconForDoc(d) {
  const k = d.kind || "";
  const ct = d.content_type || "";
  if (k === "pdf" || ct === "application/pdf") return FileText;
  if (k === "csv" || k === "sheet" || ct.includes("csv") || ct.includes("sheet") || ct.includes("excel")) return FileSpreadsheet;
  if (k === "image" || ct.startsWith("image/")) return FileImage;
  if (k === "archive") return FileArchive;
  if (k === "text" || ct.startsWith("text/")) return FileCode;
  return FileIcon;
}

/**
 * Right-side drawer that lists every uploaded file and lets the user upload
 * any file type (PDF, CSV, images, docs, etc.). If the user uploads a CSV
 * and picks a target store, the CSV is imported into inventory. Everything
 * else is archived. Admins can also empty the trash.
 */
export default function DocumentsDrawer({ open, onOpenChange, stores = [], onSuccess }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [csvStore, setCsvStore] = useState(user.role === "admin" ? "" : user.store_id);
  const [confirmDelete, setConfirmDelete] = useState(null); // doc to delete
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null); // { store_id, store_name, used_llm, rows, filename }
  const [selectedRows, setSelectedRows] = useState({}); // { product_id: quantity }
  const [initials, setInitials] = useState("");
  const inputRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load documents");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); }, [open]);

  const pickFile = () => inputRef.current?.click();

  // Also archive the raw file to /documents so it shows up in the list
  const archiveFile = async (f, storeId) => {
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (storeId) fd.append("store_id", storeId);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) {
      // Best-effort — the parse/apply already succeeded — but surface so users
      // notice the audit trail failed to record.
      console.warn("Archive failed", err);
      toast.warning("Applied, but couldn't archive the file for history");
    }
  };

  const parseFile = async (f, targetStore) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (targetStore) fd.append("store_id", targetStore);
      const { data } = await api.post("/inventory/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // seed selection with every row's suggested quantity
      const sel = {};
      (data.rows || []).forEach(r => { sel[r.product_id] = r.quantity; });
      setSelectedRows(sel);
      setPreview({ ...data, filename: f.name, file: f });
    } catch (err) {
      const reason = err.response?.data?.detail || "We could not read that file. Try a CSV or a text-based PDF.";
      toast.error("Upload not processed", { description: reason, duration: 8000 });
      // Still archive so the user has a copy
      await archiveFile(f, targetStore);
      reload();
      onSuccess && onSuccess();
    } finally { setParsing(false); }
  };

  const applyPreview = async () => {
    if (!preview) return;
    const stamp = (initials || "").trim();
    if (!stamp) {
      toast.error("Add your initials to approve");
      return;
    }
    const rows = Object.entries(selectedRows)
      .map(([product_id, quantity]) => ({ product_id, quantity: parseInt(quantity, 10) || 0 }))
      .filter(r => r.quantity > 0);
    if (!rows.length) {
      toast.info("Nothing selected");
      return;
    }
    setBusy(true);
    try {
      const body = { rows, initials: stamp };
      if (user.role === "admin" && preview.store_id) body.store_id = preview.store_id;
      const { data } = await api.post("/inventory/apply-parsed", body);
      const applied = data.applied || [];
      // Archive the source doc (tag it to the store so bookstore users can see it too)
      if (preview.file) await archiveFile(preview.file, preview.store_id);
      toast.success(`Adjusted ${applied.length} title${applied.length === 1 ? "" : "s"} in ${preview.store_name} · ${stamp}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const { data: r } = await api.post("/inventory/undo-parsed", { store_id: preview.store_id, rows: applied });
              toast.success(`Reverted ${r.reversed} title${r.reversed === 1 ? "" : "s"}`);
              onSuccess && onSuccess();
            } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
          },
        },
      });
      setPreview(null); setSelectedRows({}); setInitials("");
      reload();
      onSuccess && onSuccess();
    } catch (err) {
      toast.error("Could not apply to inventory", { description: err.response?.data?.detail || "Something went wrong while saving - nothing was changed.", duration: 8000 });
    } finally { setBusy(false); }
  };

  const cancelPreview = async () => {
    // Archive the raw file even if the user didn't apply, so they don't lose it
    if (preview?.file) { await archiveFile(preview.file, preview.store_id); reload(); onSuccess && onSuccess(); }
    setPreview(null); setSelectedRows({}); setInitials("");
  };

  const uploadFile = async (f) => {
    // Non-admin: use their own store. Admin: use selected store or archive-only.
    const targetStore = user.role === "admin" ? csvStore : user.store_id;
    if (!targetStore) {
      // No store selected → just archive the file, don't auto-adjust
      setBusy(true);
      try {
        await archiveFile(f, null);
        toast.success(`Uploaded ${f.name}`);
        reload(); onSuccess && onSuccess();
      } finally { setBusy(false); }
      return;
    }
    await parseFile(f, targetStore);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    await uploadFile(f);
  };

  const view = async (d) => {
    try {
      const { data } = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: d.content_type || "application/octet-stream" }));
      const w = window.open(url, "_blank", "noopener");
      if (!w) {
        const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
      }
    } catch { toast.error("Could not open"); }
  };

  const download = async (d) => {
    try {
      const { data } = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
    } catch { toast.error("Could not download"); }
  };

  const remove = (d) => setConfirmDelete(d);

  const doDelete = async () => {
    const d = confirmDelete;
    if (!d) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Moved to trash");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setConfirmDelete(null); }
  };

  const emptyTrash = () => setConfirmPurge(true);

  const doPurge = async () => {
    try {
      const { data } = await api.post("/documents/purge");
      const ids = data.ids || [];
      const count = data.purged || 0;
      reload();
      if (count === 0) {
        toast.info("Nothing to clear");
      } else {
        toast.success(`Cleared ${count} upload${count === 1 ? "" : "s"}`, {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const { data: r } = await api.post("/documents/restore", { ids });
                toast.success(`Restored ${r.restored} upload${r.restored === 1 ? "" : "s"}`);
                reload();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Undo failed");
              }
            },
          },
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setConfirmPurge(false); }
  };

  return (
    <>
      <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid="docdrawer-file-input"/>
      <Sheet open={open} onOpenChange={(o) => { onOpenChange(o); }}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="documents-drawer">
          <SheetHeader className="p-6 border-b border-border">
            <SheetTitle className="font-display text-2xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-accent" strokeWidth={1.5}/>
              Uploads
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Upload any file — PDFs, CSVs, images, invoices, spreadsheets. When a target store is set, the file's titles and counts are read, previewed for approval, and added to that store's inventory.
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-4 border-b border-border space-y-3">
            {user.role === "admin" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Target store <span className="text-muted-foreground normal-case">(required to auto-adjust inventory)</span></Label>
                <Select value={csvStore} onValueChange={setCsvStore}>
                  <SelectTrigger data-testid="docdrawer-store"><SelectValue placeholder="Choose store"/></SelectTrigger>
                  <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <Button onClick={pickFile} disabled={busy || parsing} className="w-full" data-testid="docdrawer-upload-btn">
              {(busy || parsing) ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Upload className="w-4 h-4 mr-2"/>}
              {parsing ? "Reading document…" : "Upload file"}
            </Button>

            {docs.length > 0 && (
              <Button variant="outline" onClick={emptyTrash} className="w-full" data-testid="docdrawer-empty-trash">
                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5}/> Clear all uploads
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>}
            {!loading && docs.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-16 px-6">
                No files yet.<br/>Upload one to see it here.
              </div>
            )}
            <div className="divide-y divide-border">
              {docs.map(d => {
                const Icon = iconForDoc(d);
                return (
                  <div key={d.id} className="px-6 py-3 flex items-start gap-3" data-testid={`docdrawer-row-${d.id}`}>
                    <div className="w-9 h-9 rounded-sm bg-secondary flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-accent" strokeWidth={1.5}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <button type="button" onClick={()=>view(d)} className="text-sm font-medium text-left truncate w-full hover:text-accent transition-colors block" data-testid={`docdrawer-view-${d.id}`}>
                        {d.name}
                      </button>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {humanSize(d.size)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}{d.store_name ? ` · ${d.store_name}` : ""} · {new Date(d.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={()=>download(d)} title="Download" data-testid={`docdrawer-dl-${d.id}`}>
                        <Download className="w-3.5 h-3.5"/>
                      </Button>
                      {(user.role === "admin" || d.uploaded_by === user.id) && (
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={()=>remove(d)} title="Delete" data-testid={`docdrawer-del-${d.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive"/>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o)=>{ if(!o) setConfirmDelete(null); }}>
        <AlertDialogContent className="border-border" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete this file?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              <span className="text-foreground font-medium">{confirmDelete?.name}</span> will be moved to the trash. An administrator can permanently wipe it later with <em>Clear all uploads</em>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-ok">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!preview} onOpenChange={(o)=>{ if(!o) cancelPreview(); }}>
        <AlertDialogContent className="border-border max-w-2xl" data-testid="import-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Review inventory changes</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              {preview?.filename ? <>From <span className="text-foreground font-medium">{preview.filename}</span>. </> : null}
              Store: <span className="text-foreground font-medium">{preview?.store_name}</span>.{" "}
              {preview?.used_llm ? "Titles were matched by AI — please double-check." : "Titles were matched directly."}
              {" "}Uncheck rows to skip. These counts REPLACE the current on-hand quantity for each title.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(preview?.rows?.length || 0) === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {preview?.message || "No titles were matched to your catalog."}
            </div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto -mx-6 px-6">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="text-left py-2 w-8"></th>
                    <th className="text-left py-2">Title</th>
                    <th className="text-right py-2 w-14">Now</th>
                    <th className="text-right py-2 w-24">New count</th>
                    <th className="text-right py-2 w-14">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview?.rows?.map(r => {
                    const included = selectedRows[r.product_id] !== undefined;
                    const qty = included ? (parseInt(selectedRows[r.product_id], 10) || 0) : 0;
                    return (
                      <tr key={r.product_id} data-testid={`preview-row-${r.sku}`}>
                        <td className="py-2">
                          <input
                            type="checkbox"
                            checked={included}
                            onChange={(e) => {
                              const s = { ...selectedRows };
                              if (e.target.checked) s[r.product_id] = r.quantity;
                              else delete s[r.product_id];
                              setSelectedRows(s);
                            }}
                            data-testid={`preview-toggle-${r.sku}`}
                            className="h-4 w-4 accent-accent"
                          />
                        </td>
                        <td className="py-2 min-w-0">
                          <div className="font-medium truncate">{r.title}</div>
                          <div className="text-[11px] text-muted-foreground font-mono truncate">{r.sku}{r.author ? ` · ${r.author}` : ""}</div>
                        </td>
                        <td className="py-2 text-right font-mono tabular-nums text-muted-foreground">{r.current_qty}</td>
                        <td className="py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            value={included ? selectedRows[r.product_id] : ""}
                            disabled={!included}
                            onChange={(e) => setSelectedRows({ ...selectedRows, [r.product_id]: e.target.value })}
                            className="w-16 text-right font-mono border border-border bg-background rounded-sm px-1 py-0.5 tabular-nums"
                            data-testid={`preview-qty-${r.sku}`}
                          />
                        </td>
                        <td className="py-2 text-right font-mono tabular-nums font-medium">{qty}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {(preview?.rows?.length || 0) > 0 && (
            <div className="pt-2 border-t border-border space-y-1.5">
              <Label htmlFor="import-initials" className="text-xs">Your initials <span className="text-destructive">*</span></Label>
              <Input
                id="import-initials"
                value={initials}
                onChange={(e) => setInitials(e.target.value.slice(0, 24))}
                placeholder="e.g. AK"
                autoFocus
                className="font-mono"
                data-testid="preview-initials-input"
              />
              <p className="text-[11px] text-muted-foreground">Saved to every transaction and audit log so we know who approved this adjustment.</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelPreview} data-testid="preview-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyPreview}
              disabled={busy || !initials.trim() || !Object.values(selectedRows).some(q => parseInt(q,10) > 0)}
              data-testid="preview-apply"
            >
              {busy ? "Applying…" : "Apply to inventory"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent className="border-border" data-testid="confirm-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Clear all upload history?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              {user.role === "admin"
                ? "This will move every uploaded file across the network to the trash — PDFs, CSVs, images, everything. Inventory is not affected."
                : "This will move every file your store has uploaded to the trash — PDFs, CSVs, images, everything. Inventory is not affected."}
              {" "}You'll have a short Undo window to bring them back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doPurge} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-purge-ok">
              Yes, wipe everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
